/** Remote-entity interpolation over the flat player/disc lists in snapshots.
 *  Samples `delayMs` behind estimated server time; extrapolates briefly. */
import { INTERP_DELAY_MS, INTERP_EXTRAP_MAX_MS } from '../../sim/constants.js'
import type { DiscStateName, PlayerStateName, Team } from '../../sim/types.js'
import type { DiscSnap, PlayerSnap, SnapshotMsg } from '../../shared/protocol.js'

export interface InterpPlayer {
  id: string; team: Team
  x: number; y: number; z: number
  ax: number; az: number
  st: PlayerStateName; stt: number; sag: number
  alive: boolean; inv: number; sta: number; held: number; pu: string | null; pus: string[]; name: string
  /** Server velocity of the newer bracket sample (05 §5 lead marker). */
  vx: number; vz: number
  /** S10 jump: grounded flag and landing-lock ticks of the newer sample (PlayerSnap.g / lt). */
  g: boolean; lt: number
}
export interface InterpDisc {
  id: number; team: Team; st: DiscStateName; th: string | null; hl: string | null
  x: number; y: number; z: number; vx: number; vz: number
}

export interface InterpOptions { delayMs?: number; maxExtrapolateMs?: number; keepMs?: number }

export class InterpBuffer {
  private buf: SnapshotMsg[] = []
  delayMs: number
  private readonly maxExtrapolateMs: number
  private readonly keepMs: number

  constructor(opts: InterpOptions = {}) {
    this.delayMs = opts.delayMs ?? INTERP_DELAY_MS
    this.maxExtrapolateMs = opts.maxExtrapolateMs ?? INTERP_EXTRAP_MAX_MS
    this.keepMs = opts.keepMs ?? 1000
  }

  get size(): number { return this.buf.length }
  get newest(): SnapshotMsg | undefined { return this.buf[this.buf.length - 1] }

  push(snap: SnapshotMsg): void {
    const last = this.buf[this.buf.length - 1]
    if (last && snap.tick <= last.tick) return
    this.buf.push(snap)
    const cutoff = snap.t - this.keepMs
    while (this.buf.length > 2 && this.buf[0].t < cutoff) this.buf.shift()
  }

  private bracket(serverNow: number): { a: SnapshotMsg; b: SnapshotMsg; f: number; extra: number } | null {
    if (!this.buf.length) return null
    const t = serverNow - this.delayMs
    let a = this.buf[0], b = this.buf[0]
    for (let i = 0; i < this.buf.length; i++) {
      if (this.buf[i].t <= t) a = this.buf[i]
      if (this.buf[i].t >= t) { b = this.buf[i]; break }
      b = this.buf[i]
    }
    if (a === b || b.t <= a.t) return { a, b: a, f: 0, extra: Math.min(Math.max(0, t - a.t), this.maxExtrapolateMs) / 1000 }
    return { a, b, f: (t - a.t) / (b.t - a.t), extra: 0 }
  }

  /** Remote players (excluding `exclude`). */
  sample(serverNow: number, exclude?: string): InterpPlayer[] {
    const br = this.bracket(serverNow)
    if (!br) return []
    const out: InterpPlayer[] = []
    for (const pb of br.b.players) {
      if (pb.id === exclude) continue
      const pa = br.a === br.b ? undefined : br.a.players.find((p) => p.id === pb.id)
      out.push(pa ? lerpPlayer(pa, pb, br.f) : playerRow(pb, br.extra))
    }
    return out
  }

  /** Discs not owned/thrown/held by `excludeLocalId` (those are predicted). */
  sampleDiscs(serverNow: number, excludeLocalId?: string): InterpDisc[] {
    const br = this.bracket(serverNow)
    if (!br) return []
    const out: InterpDisc[] = []
    for (const db of br.b.discs) {
      if (excludeLocalId && (db.hl === excludeLocalId || (db.st === 'FLIGHT' && db.th === excludeLocalId) || (db.st === 'RETURNING' && db.hl === excludeLocalId))) continue
      const da = br.a === br.b ? undefined : br.a.discs.find((d) => d.id === db.id)
      if (da && da.st === db.st) {
        out.push({ id: db.id, team: db.team, st: db.st, th: db.th, hl: db.hl, x: lerp(da.x, db.x, br.f), y: lerp(da.y, db.y, br.f), z: lerp(da.z, db.z, br.f), vx: db.vx, vz: db.vz })
      } else {
        out.push({ id: db.id, team: db.team, st: db.st, th: db.th, hl: db.hl, x: db.x + db.vx * br.extra, y: db.y, z: db.z + db.vz * br.extra, vx: db.vx, vz: db.vz })
      }
    }
    return out
  }
}

const lerp = (a: number, b: number, f: number): number => a + (b - a) * f
function playerRow(p: PlayerSnap, extraSec: number): InterpPlayer {
  return { id: p.id, team: p.team, x: p.x + p.vx * extraSec, y: p.y, z: p.z + p.vz * extraSec, ax: p.ax, az: p.az, st: p.st, stt: p.stt, sag: p.sag, alive: p.alive === 1, inv: p.inv, sta: p.sta, held: p.held.length, pu: p.pu[0] ?? null, pus: p.pu.slice(), name: p.name, vx: p.vx, vz: p.vz, g: p.g === 1, lt: p.lt }
}
function lerpPlayer(a: PlayerSnap, b: PlayerSnap, f: number): InterpPlayer {
  const ax = lerp(a.ax, b.ax, f), az = lerp(a.az, b.az, f)
  const l = Math.hypot(ax, az) || 1
  return { id: b.id, team: b.team, x: lerp(a.x, b.x, f), y: lerp(a.y, b.y, f), z: lerp(a.z, b.z, f), ax: ax / l, az: az / l, st: b.st, stt: b.stt, sag: b.sag, alive: b.alive === 1, inv: b.inv, sta: lerp(a.sta, b.sta, f), held: b.held.length, pu: b.pu[0] ?? null, pus: b.pu.slice(), name: b.name, vx: b.vx, vz: b.vz, g: b.g === 1, lt: b.lt }
}
export type { DiscSnap }
